import { useRef, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { kebabCase } from "lodash";
import toast from "react-hot-toast";

import { BoxedContent } from "@/components/common";
import { DynamicFormContainer } from "@/components/dynamic";
import dynamicObjectMap from "@/utils/maps/dynamicObjectMap";
import useTaskRedirect from "@/hooks/useTaskRedirect";
import { getFullName, calculateTax } from "@/utils/helpers";
import {
  calculateContractRefund,
  getSecurityDeposit,
} from "@/utils/leasing/helpers";
import { useCompanyAccount } from "@/hooks";

import { getDynamicObjectRecordById } from "@/api/dynamic/dynamicObjectNameApi";

const formatVisualInspection = (visualInspection) => {
  const data = {
    chargeItem: [],
  };

  if (visualInspection.defect && visualInspection.defect.length) {
    visualInspection.defect.forEach((defect) => {
      const item = {
        type: {
          label: "Damage Penalty",
          value: "DamagePenalty",
        },
        account: null,
        amount: defect.totalAmount,
        tax: null,
        taxAmount: 0,
        totalAmount: defect.totalAmount,
      };

      if (defect.totalAmount) {
        data.chargeItem.push(item);
      }
    });
  }

  return data;
};

const formatContract = (contract) => {
  const securityDeposit = getSecurityDeposit(contract);

  return {
    tenant: {
      ...contract.tenant,
      label: getFullName(contract.tenant),
      value: contract.tenant.id,
    },
    contract: {
      label: contract.number,
      value: contract.id,
      agreementStartDate: contract.agreementStartDate,
      agreementEndDate: contract.agreementEndDate,
      contractTerminationDate: contract.contractTerminationDate,
      securityDeposit,
      earlyTerminationPenalty: contract.earlyTerminationPenalty,
    },
    securityDeposit,
    rentForPeriod: contract.totalContractAmount,
    utilityAmountForPeriod: contract.totalUtilityCharge,
    paymentStatus: {
      label: "Pending",
      value: "Pending",
    },
  };
};

const calculateContractCharges = (contract, visualInspectionData) => {
  const { damagePenaltyAccount } = contract;

  const data = {
    chargeItem: [],
    totalCharge: 0,
  };

  if (visualInspectionData) {
    const formattedVisualInspection =
      formatVisualInspection(visualInspectionData);
    data.chargeItem = data.chargeItem.concat(
      formattedVisualInspection.chargeItem,
    );
    data.chargeItem = data.chargeItem.map((c) => ({
      ...c,
      account: {
        label: damagePenaltyAccount.name,
        value: damagePenaltyAccount.id,
      },
    }));
  }

  data.totalCharge = data.chargeItem.reduce(
    (prevValue, { totalAmount }) => prevValue + totalAmount,
    0,
  );

  return data;
};

function ContractTerminationForm() {
  const ref = useRef(null);
  const [searchParams] = useSearchParams();
  const [initialState, setInitialState] = useState({});
  // TODO: Temporary fix, remove later
  const [contractId, setContractId] = useState("");
  const { redirect } = useTaskRedirect();
  const defaultAccounts = useCompanyAccount({
    params: {
      includeCompanyId: true,
      isLinkedWithRecord: false,
    },
  });

  const visualInspection = searchParams.get("visualInspection");
  const moveOutRequest = searchParams.get("moveOutRequest");

  const { data: visualInspectionData } = useQuery(
    [
      kebabCase(dynamicObjectMap.get("VisualInspectionObjectName")),
      visualInspection,
      moveOutRequest,
    ],
    () =>
      getDynamicObjectRecordById(
        kebabCase(dynamicObjectMap.get("VisualInspectionObjectName")),
        visualInspection,
      ),
    {
      enabled: Boolean(visualInspection),
    },
  );

  const { data: moveOutRequestData } = useQuery(
    [
      kebabCase(dynamicObjectMap.get("MoveOutRequestObjectName")),
      moveOutRequest,
    ],
    () =>
      getDynamicObjectRecordById(
        kebabCase(dynamicObjectMap.get("MoveOutRequestObjectName")),
        moveOutRequest,
      ),
    {
      enabled: Boolean(moveOutRequest),
    },
  );

  useEffect(() => {
    if (moveOutRequestData) {
      setContractId(moveOutRequestData?.contract?.id);
    }
  }, [moveOutRequestData]);

  const { data: contractData } = useQuery(
    [
      kebabCase(dynamicObjectMap.get("ContractObjectName")),
      "move-out-request",
      contractId,
    ],
    () =>
      getDynamicObjectRecordById(
        kebabCase(dynamicObjectMap.get("ContractObjectName")),
        contractId,
      ),
    {
      enabled: Boolean(contractId),
    },
  );

  const setRentDetails = async () => {
    const moveOutDate = new Date(moveOutRequestData?.mODate);

    const chargesData = calculateContractCharges(
      contractData,
      visualInspectionData,
    );
    const refundData = await calculateContractRefund(
      contractData,
      moveOutDate,
      chargesData.totalCharge,
    );

    setInitialState((prevState) => ({
      ...prevState,
      ...refundData,
      ...chargesData,
    }));
  };

  // TODO: Temporary fix, remove later
  const updateRentDetails = async () => {
    const formState = ref.current.getState();
    const { moveOutDate, totalCharge } = formState;

    const refundData = await calculateContractRefund(
      contractData,
      moveOutDate ? new Date(moveOutDate) : new Date(),
      totalCharge,
    );

    ref.current.setFormState(refundData);
  };

  useEffect(() => {
    if (contractData) {
      if (moveOutRequest) {
        const formattedContract = formatContract(contractData);
        setInitialState((prevState) => ({
          ...prevState,
          ...formattedContract,
        }));
        setRentDetails();
      } else {
        // TODO: Temporary fix, remove later
        updateRentDetails();
      }
    }
  }, [contractData]);

  const prepareContractTerminationCharge = (key, value, item) => {
    const formState = ref.current.getState();
    formState[key] = value;

    const { amountOfTax } = formState;
    let { amount } = item;
    const { tax, taxAmount: prevTaxAmount } = item;
    let taxAmount = 0;

    if (tax && amountOfTax) {
      const parameters = {
        amount,
        amountOfTax,
        tax,
        taxAmount: "",
      };

      if (key === "amountOfTax") {
        parameters.taxAmount = prevTaxAmount || 0;
      }

      const { taxAmount: calculatedTaxAmount, principalAmount } =
        calculateTax(parameters);

      amount = principalAmount;
      taxAmount = calculatedTaxAmount;
    }

    return {
      ...item,
      amount,
      tax,
      taxAmount,
      totalAmount: Number(amount) + Number(taxAmount),
    };
  };

  const setContractTerminationCharge = (key, value) => {
    const calculatedItemDetail = prepareContractTerminationCharge(
      key,
      value,
      value,
    );
    ref.current.setFormValue(key, calculatedItemDetail);
  };

  const setTotalCharge = (key, value) => {
    const formState = ref.current.getState();
    formState[key] = value;

    const { chargeItem, balanceAmount, totalRefundVatAmount } = formState;

    const data = {};

    const updatedChargeItem = chargeItem.map((item) =>
      prepareContractTerminationCharge(key, value, item),
    );

    data.chargeItem = updatedChargeItem;

    data.totalCharge = updatedChargeItem.reduce(
      (prevValue, { totalAmount }) => prevValue + totalAmount,
      0,
    );

    data.totalRefundAmount = balanceAmount - data.totalCharge;

    let outstandingAmount = 0;

    if (data.totalRefundAmount > 0) {
      outstandingAmount = data.totalRefundAmount + (totalRefundVatAmount ?? 0);
    }

    data.outstandingAmount = outstandingAmount;

    ref.current.setFormState(data);
  };

  const setBalanceAmount = (key, value) => {
    const formState = ref.current.getState();
    formState[key] = value;
    const {
      rentForStayedPeriod,
      totalRentReceived,
      rentVatForStayedPeriod,
      totalRentVatReceived,
      utilityAmountForStayedPeriod,
      totalUtilityAmountReceived,
      utilityVatAmountForStayedPeriod,
      totalUtilityVatAmountReceived,
      totalCharge,
    } = formState;

    const securityDeposit = getSecurityDeposit(contractData);

    const rentRefund = Number(totalRentReceived) - Number(rentForStayedPeriod);
    const rentVatRefund =
      Number(totalRentVatReceived) - Number(rentVatForStayedPeriod);
    const utilityAmountRefund =
      Number(totalUtilityAmountReceived) - Number(utilityAmountForStayedPeriod);
    const utilityVatAmountRefund =
      Number(totalUtilityVatAmountReceived) -
      Number(utilityVatAmountForStayedPeriod);

    const balanceAmount =
      rentRefund + utilityAmountRefund + Number(securityDeposit || 0);
    const totalRefundAmount = balanceAmount - Number(totalCharge || 0);
    const totalRefundVatAmount = rentVatRefund + utilityVatAmountRefund;

    let outstandingAmount = 0;

    if (totalRefundAmount > 0) {
      outstandingAmount = totalRefundAmount + totalRefundVatAmount;
    }

    ref.current.setFormState({
      securityDeposit,
      rentRefund,
      rentVatRefund,
      utilityAmountRefund,
      utilityVatAmountRefund,
      balanceAmount,
      totalRefundAmount,
      outstandingAmount,
      totalRefundVatAmount,
    });
  };

  const setEarlyTerminationPenalty = (key, value) => {
    if (!value) return;

    const formState = ref.current.getState();
    const { chargeItem, totalCharge } = formState;

    // Do not add early termination charge if already added
    if (chargeItem && chargeItem.length) {
      const earlyTerminationItem = chargeItem.find(
        (c) => c.type.value === "EarlyTermination",
      );
      if (earlyTerminationItem) {
        return;
      }
    }

    const { earlyTerminationPenalty, contractTerminationAccount } =
      contractData;

    const earlyTerminationCharge = {
      type: {
        label: "Early Termination",
        value: "EarlyTermination",
      },
      account: {
        label: contractTerminationAccount.name,
        value: contractTerminationAccount.id,
      },
      amount: earlyTerminationPenalty ?? 500,
      tax: null,
      taxAmount: 0,
      totalAmount: earlyTerminationPenalty ?? 500,
    };

    const data = {};

    if (!chargeItem || !chargeItem.length) {
      data.chargeItem = [earlyTerminationCharge];
      data.totalCharge = earlyTerminationPenalty;
    }

    if (chargeItem && chargeItem.length) {
      data.chargeItem = [...chargeItem, earlyTerminationCharge];
      data.totalCharge = Number(totalCharge) + earlyTerminationPenalty;
    }

    ref.current.setFormState(data);
    setBalanceAmount("totalCharge", data.totalCharge);
  };

  const onStateChange = (key, value) => {
    switch (key) {
      // TODO: Temporary fix, remove later
      case "contract":
        if (value) {
          setContractId(value.value);
        }
        break;
      case "addEarlyTerminationPenalty":
        setEarlyTerminationPenalty(key, value);
        break;

      case "chargeItem":
        setTotalCharge(key, value);
        break;

      case "chargeItemTable":
        setContractTerminationCharge(key, value);
        break;

      case "rentForStayedPeriod":
      case "rentVatForStayedPeriod":
      case "utilityAmountForStayedPeriod":
      case "utilityVatAmountForStayedPeriod":
        setBalanceAmount(key, value);
        break;

      default:
        break;
    }
  };

  const onChildStateChange = ({
    index,
    key,
    value,
    parentField,
    parentFieldType,
  }) => {
    const formState = ref.current.getState();
    const stateKey = `${parentField}${parentFieldType}`;
    let parentFieldState = formState[stateKey] ?? {};

    if (index > -1) {
      parentFieldState = formState[parentField][index];
    }

    if (parentField === "chargeItem") {
      switch (key) {
        case "type": {
          parentFieldState[key] = value;
          const { type } = parentFieldState;
          const { contractBreakPenalty, otherIncome, damageBreakPenalty } =
            defaultAccounts;

          let account = otherIncome;

          if (type?.value === "EarlyTermination") {
            account = contractBreakPenalty;
          }

          if (type?.value === "DamagePenalty") {
            account = damageBreakPenalty;
          }

          parentFieldState.account = account;

          break;
        }

        default:
          break;
      }
    }
  };

  const onBeforeSave = () => {
    const formState = ref.current.getState();
    const { chargeItem } = formState;

    const itemsWithZeroAmount = chargeItem.filter(
      (o) => Number(o.totalAmount || 0) === 0,
    );

    if (itemsWithZeroAmount.length) {
      toast.error(
        `Amount for one or more charge items is 0.
         Please enter a larger number or remove the item from table.`,
      );
      return false;
    }

    const lineItemsWithoutTax = chargeItem.filter((o) => Boolean(!o.tax));

    if (lineItemsWithoutTax.length) {
      toast.error("Please select tax for all line items");
      return false;
    }

    const lineItemsWithoutDescription = chargeItem.filter((o) => Boolean(!o.description));

    if (lineItemsWithoutDescription.length) {
      toast.error("Please enter a description for all penalties & charges");
      return false;
    }

    return true;
  };

  return (
    <BoxedContent>
      <DynamicFormContainer
        ref={ref}
        initialData={initialState}
        onStateChange={onStateChange}
        onChildStateChange={onChildStateChange}
        objectName={dynamicObjectMap.get("ContractTerminationObjectName")}
        showHeader
        onSuccess={(recordId) =>
          redirect(-1, {
            recordId,
            success: true,
          })}
        navigate={false}
        readOnlyFields={moveOutRequest ? ["moveOutDate"] : []}
        onBeforeSave={onBeforeSave}
      />
    </BoxedContent>
  );
}

export default ContractTerminationForm;
